import { useState, Suspense, lazy, useMemo, useCallback, useEffect, useRef } from 'react';
import { AppErrorBoundary } from '@/components/common/AppErrorBoundary';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Project } from '@/types/project';
import { ProjectPermission } from '@/types/auth';
import { CheckSquare, DollarSign, Users, FileText, Loader2, Building2, Ruler, TrendingUp, FileCheck, Calendar, Bot, ListChecks, ClipboardList, ClipboardCheck, Flag, Activity, Shield, FileBarChart, Wrench, Receipt, Layers, Shapes } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import TabGroupWrapper from './TabGroupWrapper';
import { HIDE_CONTROL_UI } from './signoff/hideControlUI';
import { useProjectStateManager } from '@/hooks/useProjectStateManager';
import { Skeleton } from '@/components/ui/skeleton';
import { useQueryClient } from '@tanstack/react-query';
import { useProcurementAccess } from '@/hooks/useProcurementPermissions';
import { useCollectionAccess } from '@/hooks/collection/useCollectionPermissions';
import { usePlanAccess, useResourceAccess, useExecuteAccess, useAIAdvisorAccess, useNeedsAccess, useQAQCAccess, useClosingAccess } from '@/hooks/useProjectTabsPermissions';
import { useSpecsAccess } from '@/hooks/useSpecsPermissions';
import { useDrawingsAccess } from '@/hooks/drawings/useDrawingPermissions';
import { useCheckPermission } from '@/hooks/useUserRoles';
import { usePermitAccess } from '@/hooks/permit/usePermitPermissions';
import { usePLAccess } from '@/hooks/pl/usePLPermissions';
import { useMeetingAccess } from '@/hooks/meeting/useMeetingPermissions';
import { useReportsAccess } from '@/hooks/useReportsPermissions';
import { globalFeatureFlags } from '@/lib/featureFlags';

// Lazy load heavy components
const ProjectItemsManagement = lazy(() => import('./ProjectItemsManagement'));
const ProjectTeamManagement = lazy(() => import('./ProjectTeamManagement'));
const ProjectDiary = lazy(() => import('../diary/ProjectDiary'));
const SpecificationsView = lazy(() => import('../diary/SpecificationsView'));
const OptimizedProcurementManagement = lazy(() => import('../procurement/OptimizedProcurementManagement'));
const PaymentDocsTab = lazy(() => import('../procurement/PaymentDocsTab'));
const CollectionManagement = lazy(() => import('../collection/CollectionManagement'));
const PLManagement = lazy(() => import('../pl/PLManagement'));
const DrawingsLayout = lazy(() => import('../drawings/DrawingsLayout'));
const PermitLayout = lazy(() => import('../permit/PermitLayout'));
const MeetingManagement = lazy(() => import('../meeting/MeetingManagement'));
const AIAdvisorInlineTabs = lazy(() => import('../ai-advisor/AIAdvisorInlineTabs'));
const NeedsLayout = lazy(() => import('../needs/NeedsLayout'));
const QAQCView = lazy(() => import('../qaqc/QAQCView'));
const ClosingManagement = lazy(() => import('../closing/ClosingManagement'));
const ProjectActivityLog = lazy(() => import('./activity/ProjectActivityLog'));
const ControlDashboard = lazy(() => import('./control/ControlDashboard'));
const ReportsManagement = lazy(() => import('../reports/ReportsManagement'));
const MaintainSection = lazy(() => import('./maintain/MaintainSection'));
const MethodSection = lazy(() =>
  import('@/components/projects/method/MethodSection').then((m) => ({
    default: m.MethodSection,
  }))
);
const LayoutSection = lazy(() =>
  import('@/components/projects/method/LayoutSection').then((m) => ({
    default: m.LayoutSection,
  }))
);

// ProcurementErrorBoundary is now included in OptimizedProcurementManagement

interface ProjectContentProps {
  project: Project;
  permissions: ProjectPermission;
}

const ProjectContent = ({ project, permissions }: ProjectContentProps) => {
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getProjectState, saveActiveTab, saveScrollPosition } = useProjectStateManager(project.id);
  const canAccessActivity = useCheckPermission('ACTIVITY_LOG_ACCESS').data ?? false;
  
  // Track user action vs programmatic change to prevent race conditions
  const isUserActionRef = useRef(false);
  
  // ✅ Phase 3: Check all tab access permissions with isLoading states
  const { data: canAccessPlan, isLoading: isLoadingPlan } = usePlanAccess();
  const { data: canAccessResource, isLoading: isLoadingResource } = useResourceAccess();
  const { data: canAccessExecute, isLoading: isLoadingExecute } = useExecuteAccess();
  const { data: canAccessSpecs, isLoading: isLoadingSpecs } = useSpecsAccess();
  const { data: hasProcurementAccess, isLoading: isLoadingPermission } = useProcurementAccess(project.id);
  const { data: hasCollectionAccess, isLoading: isLoadingCollection } = useCollectionAccess(project.id);
  const { data: canAccessDrawings, isLoading: isLoadingDrawings } = useDrawingsAccess();
  const { data: canAccessPermit, isLoading: isLoadingPermit } = usePermitAccess();
  // P&L permission check (only when feature flag enabled)
  const ENABLE_PL_PERMISSIONS_FLAG = globalFeatureFlags.isEnabled('ENABLE_PL_PERMISSIONS');
  const { data: hasPLAccess, isLoading: isLoadingPL } = usePLAccess(project.id);
  
   // ✅ RETIRED 2026-05-01 (PR-F2): ENABLE_MEETING_MODULE flag removed — always check meeting access
  const { data: hasMeetingAccess, isLoading: isLoadingMeeting } = useMeetingAccess(project.id);
  
  // Feature flag for strict permission checks (default: false for non-regression)
  const ENABLE_STRICT_PERMISSION_CHECK = import.meta.env.VITE_STRICT_PERMISSION_CHECK === 'true';
  
  // Feature flag for Procurement tab (default: true for this implementation)
  const ENABLE_PROCUREMENT_TAB = true && hasProcurementAccess;
  const ENABLE_PAYMENT_DOCS_TAB = true && hasProcurementAccess;
  
  // Feature flag for Collection tab
  const ENABLE_COLLECTION_TAB = globalFeatureFlags.isEnabled('ENABLE_COLLECTION_MODULE') && hasCollectionAccess;
  
  // ✅ Light QA: Fallback to old logic when flag OFF
  const ENABLE_PL_TAB = ENABLE_PL_PERMISSIONS_FLAG
    ? (globalFeatureFlags.isEnabled('ENABLE_COLLECTION_MODULE') && hasPLAccess)
    : (ENABLE_COLLECTION_TAB && hasProcurementAccess); // Legacy logic
  
  // ✅ Feature flag for Meeting tab (Light QA Contract Compliant)
  const ENABLE_MEETING_TAB = hasMeetingAccess;
  
  // ✅ Feature flag for AI Advisor tab (Light QA Contract Compliant)
  // Requires BOTH: feature flag enabled AND user has AI_ADVISOR_ACCESS permission (or is admin)
  const { data: hasAIAdvisorAccess, isLoading: isLoadingAIAdvisor } = useAIAdvisorAccess();
  const ENABLE_AI_ADVISOR_TAB = hasAIAdvisorAccess;
  
  // ✅ HARDCODED (PR-F1 2026-04-30): ENABLE_NEEDS_TAB + ENABLE_QAQC_TAB retired, permission-only gates
  const { data: canAccessNeeds, isLoading: isLoadingNeeds } = useNeedsAccess();
  
  const { data: canAccessQAQC, isLoading: isLoadingQAQC } = useQAQCAccess();
  
  // ✅ Closing tab permission check (Light QA Contract Compliant)
  const ENABLE_CLOSING_TAB = globalFeatureFlags.isEnabled('ENABLE_CLOSING_TAB');
  const { data: canAccessClosing, isLoading: isLoadingClosing } = useClosingAccess();

  // ✅ Reports tab permission check (Session 1 Foundation)
  const { data: canAccessReports, isLoading: isLoadingReports } = useReportsAccess();

  // ✅ Activity Log tab (Control Center Phase 1)
  const ENABLE_ACTIVITY_LOG = globalFeatureFlags.isEnabled('ENABLE_ACTIVITY_LOG');
  
  // ✅ Control tab (Control Center Phase 3)
  const ENABLE_SIGN_OFF_SYSTEM = globalFeatureFlags.isEnabled('ENABLE_SIGN_OFF_SYSTEM');
  
  // Check if permissions are still loading
  const isLoadingPermissions = isLoadingPlan || isLoadingResource || isLoadingExecute || 
    isLoadingSpecs || isLoadingDrawings || isLoadingPermit || isLoadingCollection || 
    (ENABLE_PL_PERMISSIONS_FLAG ? isLoadingPL : false) ||
    isLoadingMeeting ||
    isLoadingAIAdvisor ||
    isLoadingNeeds ||
    isLoadingQAQC ||
    (ENABLE_CLOSING_TAB ? isLoadingClosing : false) ||
    isLoadingReports;
  
  // ✅ Service project flag — gates Maintenance tab
  const isServiceProject = project.status === 'service';

  // ✅ Calculate available tabs based on permissions
  const availableTabs = useMemo(() => {
    if (ENABLE_STRICT_PERMISSION_CHECK && isLoadingPermissions) return [];
    
    const tabs = [];
    // ✅ Needs tab (TRƯỚC tasks/Plan)
    if (canAccessNeeds) tabs.push('needs');
    tabs.push('scope');
    // Strict mode: use === true check, Legacy mode: use truthy check
    if (ENABLE_STRICT_PERMISSION_CHECK ? canAccessPlan === true : !!canAccessPlan) tabs.push('tasks');
    tabs.push('method');
    tabs.push('layout');
    if (ENABLE_STRICT_PERMISSION_CHECK ? canAccessResource === true : !!canAccessResource) tabs.push('team');
    if (ENABLE_STRICT_PERMISSION_CHECK ? canAccessExecute === true : !!canAccessExecute) tabs.push('diary');
    if (ENABLE_STRICT_PERMISSION_CHECK ? canAccessSpecs === true : !!canAccessSpecs) tabs.push('specifications');
    if (canAccessQAQC) tabs.push('qaqc');
    if (ENABLE_PROCUREMENT_TAB) tabs.push('procurement');
    if (ENABLE_PAYMENT_DOCS_TAB) tabs.push('payment-docs');
    if (ENABLE_COLLECTION_TAB) tabs.push('collection');
    if (ENABLE_PL_TAB) tabs.push('pl');
    if (ENABLE_STRICT_PERMISSION_CHECK ? canAccessDrawings === true : !!canAccessDrawings) tabs.push('drawings');
    if (canAccessPermit) tabs.push('permit');
    if (ENABLE_MEETING_TAB) tabs.push('meeting');
    if (ENABLE_CLOSING_TAB && canAccessClosing) tabs.push('closing');
    if (canAccessReports) tabs.push('reports');
    if (!HIDE_CONTROL_UI && ENABLE_SIGN_OFF_SYSTEM) tabs.push('control');
    if (ENABLE_ACTIVITY_LOG && canAccessActivity) tabs.push('activity');
    if (ENABLE_AI_ADVISOR_TAB) tabs.push('ai-advisor');
    if (isServiceProject) tabs.push('maintenance');
    return tabs;
  }, [canAccessNeeds, canAccessPlan, canAccessResource, canAccessExecute, canAccessSpecs, canAccessQAQC, canAccessDrawings, canAccessPermit, ENABLE_PROCUREMENT_TAB, ENABLE_PAYMENT_DOCS_TAB, ENABLE_COLLECTION_TAB, ENABLE_PL_TAB, ENABLE_MEETING_TAB, ENABLE_CLOSING_TAB, canAccessClosing, canAccessReports, ENABLE_SIGN_OFF_SYSTEM, ENABLE_ACTIVITY_LOG, ENABLE_AI_ADVISOR_TAB, ENABLE_STRICT_PERMISSION_CHECK, isLoadingPermissions, isServiceProject, canAccessActivity]);
  
  const [activeTab, setActiveTab] = useState(() => {
    // Priority 1: URL query parameter (highest)
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && availableTabs.includes(tabFromUrl)) {
      return tabFromUrl;
    }
    // Priority 2: localStorage fallback
    const savedTab = getProjectState().activeTab;
    if (availableTabs.includes(savedTab)) {
      return savedTab;
    }
    // Priority 3: first available tab
    return availableTabs[0] || 'tasks';
  });

  const safeActiveTab = availableTabs.includes(activeTab) ? activeTab : availableTabs[0];

  const [visited, setVisited] = useState<Set<string>>(() => safeActiveTab ? new Set([safeActiveTab]) : new Set());

  useEffect(() => {
    if (safeActiveTab) setVisited(prev => prev.has(safeActiveTab) ? prev : new Set(prev).add(safeActiveTab));
  }, [safeActiveTab]);

  
  
  // ✅ Auto-switch when permissions change
  useEffect(() => {
    if (!availableTabs.includes(activeTab) && availableTabs.length > 0) {
      const fallbackTab = availableTabs[0];
      if (import.meta.env.DEV) {
        console.log(`🔄 Auto-switching from invalid tab "${activeTab}" to "${fallbackTab}"`);
      }
      setActiveTab(fallbackTab);
      saveActiveTab(fallbackTab);
      setSearchParams({ tab: fallbackTab }); // Also update URL
    }
  }, [availableTabs, activeTab, saveActiveTab, setSearchParams]);
  
  // ✅ Sync URL tab parameter after permissions are loaded (Priority #1)
  useEffect(() => {
    // Skip if change was from user action (already handled in handleTabChange)
    if (isUserActionRef.current) {
      isUserActionRef.current = false;
      return;
    }
    
    const tabFromUrl = searchParams.get('tab');
    // More strict check: verify tab is available BEFORE syncing
    if (tabFromUrl && availableTabs.length > 0 && availableTabs.includes(tabFromUrl) && activeTab !== tabFromUrl) {
      if (import.meta.env.DEV) {
        console.log('🔄 Syncing URL tab:', tabFromUrl);
      }
      setActiveTab(tabFromUrl);
      saveActiveTab(tabFromUrl);
    }
  }, [availableTabs, searchParams, activeTab, saveActiveTab]);
  
  // Debug logging for permission checks (only when flag enabled)
  useEffect(() => {
    if (import.meta.env.VITE_DEBUG_PERMISSIONS === 'true') {
      console.log('[ProjectContent] Permission check results:', {
        canAccessPlan,
        canAccessResource,
        canAccessExecute,
        canAccessSpecs,
        isLoadingPlan,
        isLoadingResource,
        isLoadingExecute,
        isLoadingSpecs,
        strictMode: ENABLE_STRICT_PERMISSION_CHECK,
        availableTabs,
      });
    }
  }, [canAccessPlan, canAccessResource, canAccessExecute, canAccessSpecs, isLoadingPlan, isLoadingResource, isLoadingExecute, isLoadingSpecs, ENABLE_STRICT_PERMISSION_CHECK, availableTabs]);

  // ✅ Phase 2 Fix: Smart cache-aware prefetch to avoid double prefetching
  const smartPrefetch = useCallback((queryKey: any[]) => {
    const cached = queryClient.getQueryData(queryKey);
    const state = queryClient.getQueryState(queryKey);
    
    // Only prefetch if: No cache OR cache is stale (> 5 minutes)
    if (!cached || (state && Date.now() - state.dataUpdatedAt > 5 * 60 * 1000)) {
      queryClient.prefetchQuery({ 
        queryKey, 
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000, // 30 minutes for external API data
      });
    }
  }, [queryClient]);

  const handleTabChange = useCallback((value: string) => {
    // ✅ Guard: Only allow switching to available tabs
    if (!availableTabs.includes(value)) {
      if (import.meta.env.DEV) {
        console.warn(`[ProjectContent] Tab "${value}" not available, staying on "${activeTab}"`);
      }
      return;
    }
    
    if (import.meta.env.DEV) {
      console.log('[ProjectContent] Tab change requested:', {
        from: activeTab,
        to: value,
        availableTabs,
        isAllowed: availableTabs.includes(value)
      });
    }
    
    isUserActionRef.current = true; // Mark as user action
    setActiveTab(value);
    saveActiveTab(value);
    setSearchParams({ tab: value }); // ✨ Sync URL with active tab
    
    // ✅ Phase 2 Fix: Use smart prefetch instead of aggressive prefetch
    switch (value) {
      case 'tasks':
        smartPrefetch(['items-optimized', project.id]);
        break;
      case 'team':
        smartPrefetch(['project-team', project.id]);
        break;
      case 'diary':
        smartPrefetch(['diary-data', project.id]);
        break;
      case 'specifications':
        smartPrefetch(['specs-data', project.id]);
        break;
      case 'procurement':
        smartPrefetch(['procurement', 'project', project.id]);
        break;
      case 'payment-docs':
        smartPrefetch(['payment-docs', 'project', project.id]);
        break;
    }
  }, [availableTabs, activeTab, project.id, saveActiveTab, queryClient, setSearchParams, smartPrefetch]);
  
  // Restore scroll position when returning to tab
  useEffect(() => {
    const savedPosition = getProjectState().scrollPosition;
    if (savedPosition > 0) {
      window.scrollTo({ top: savedPosition, behavior: 'smooth' });
    }
  }, [activeTab, getProjectState]);
  
  // Save scroll position before leaving
  useEffect(() => {
    const handleScroll = () => {
      saveScrollPosition(window.scrollY);
    };
    
    const handleBeforeUnload = () => {
      saveScrollPosition(window.scrollY);
    };
    
    const debouncedScroll = debounce(handleScroll, 300);
    window.addEventListener('scroll', debouncedScroll);
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('scroll', debouncedScroll);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Save position on component unmount
      saveScrollPosition(window.scrollY);
    };
  }, [saveScrollPosition]);

  // Memoized loading fallback component
  const TabLoadingFallback = useMemo(() => () => (
    <div className="space-y-4">
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  ), []);

  // ✅ Phase 1 Fix: Removed tabContent useMemo - now using forceMount TabsContent for state preservation

  // Show loading skeleton while permissions load (strict mode only)
  if (ENABLE_STRICT_PERMISSION_CHECK && isLoadingPermissions) {
    return <TabLoadingFallback />;
  }


  // Tab group definitions for Smart Tab Grouping
  const TAB_GROUPS = [
    { id: 'init', tabIds: ['needs', 'scope', 'tasks', 'method', 'layout', 'team'] },
    { id: 'exec', tabIds: ['diary', 'specifications', 'qaqc', 'drawings', 'permit'] },
    { id: 'finance', tabIds: ['procurement', 'payment-docs', 'collection', 'pl'] },
    { id: 'summary', tabIds: ['meeting', 'closing', 'reports', ...(HIDE_CONTROL_UI ? [] : ['control']), 'activity', 'ai-advisor', 'maintenance'] },
  ];
  const activeGroupId = TAB_GROUPS.find(g => g.tabIds.includes(safeActiveTab))?.id;

  const tabTriggerClass = "flex items-center gap-1 md:gap-2 px-2 md:px-3 py-2 whitespace-nowrap text-sm font-semibold shrink-0 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md";

  return (
    <Tabs value={safeActiveTab} onValueChange={handleTabChange} className="w-full">
      <TabsList className="sticky top-0 z-30 bg-background !flex flex-nowrap justify-start w-full h-auto min-h-[2.75rem] gap-0 px-0 py-1.5 overflow-x-auto scrollbar-hide touch-pan-x items-end">
        
        {/* Group 1: Khởi tạo */}
        <TabGroupWrapper groupLabel="Initial & Planning" groupColor="#6366F1" isActiveGroup={activeGroupId === 'init'}>
          {canAccessNeeds && (
            <TabsTrigger 
              value="needs" 
              className={tabTriggerClass}
              onMouseEnter={() => smartPrefetch(['needs-files', project.id, null])}
            >
              <ClipboardList className={`h-4 w-4 ${isMobile ? 'hidden' : 'flex'}`} />
              Needs
            </TabsTrigger>
          )}
          <TabsTrigger value="scope" className={tabTriggerClass}>
            <ListChecks className={`h-4 w-4 ${isMobile ? 'hidden' : 'flex'}`} />
            Scope
          </TabsTrigger>
          {(ENABLE_STRICT_PERMISSION_CHECK ? canAccessPlan === true : !!canAccessPlan) && (
            <TabsTrigger value="tasks" className={tabTriggerClass}>
              <CheckSquare className={`h-4 w-4 ${isMobile ? 'hidden' : 'flex'}`} />
              Plan
            </TabsTrigger>
          )}
          <TabsTrigger value="method" className={tabTriggerClass}>
            <Layers className={`h-4 w-4 ${isMobile ? 'hidden' : 'flex'}`} />
            Method
          </TabsTrigger>
          <TabsTrigger value="layout" className={tabTriggerClass}>
            <Shapes className={`h-4 w-4 ${isMobile ? 'hidden' : 'flex'}`} />
            Layout
          </TabsTrigger>
          {(ENABLE_STRICT_PERMISSION_CHECK ? canAccessResource === true : !!canAccessResource) && (
            <TabsTrigger value="team" className={tabTriggerClass}>
              <Users className={`h-4 w-4 ${isMobile ? 'hidden' : 'flex'}`} />
              Resource
            </TabsTrigger>
          )}
        </TabGroupWrapper>

        {/* Group 2: Triển khai */}
        <TabGroupWrapper groupLabel="Development" groupColor="#F59E0B" isActiveGroup={activeGroupId === 'exec'} showSeparator>
          {(ENABLE_STRICT_PERMISSION_CHECK ? canAccessExecute === true : !!canAccessExecute) && (
            <TabsTrigger value="diary" className={tabTriggerClass}>
              <FileText className={`h-4 w-4 ${isMobile ? 'hidden' : 'flex'}`} />
              Execute
            </TabsTrigger>
          )}
          {(ENABLE_STRICT_PERMISSION_CHECK ? canAccessSpecs === true : !!canAccessSpecs) && (
            <TabsTrigger value="specifications" className={tabTriggerClass}>
              <DollarSign className={`h-4 w-4 ${isMobile ? 'hidden' : 'flex'}`} />
              {isMobile ? 'Specs' : 'Specifications'}
            </TabsTrigger>
          )}
          {canAccessQAQC && (
            <TabsTrigger value="qaqc" className={tabTriggerClass}>
              <ClipboardCheck className={`h-4 w-4 ${isMobile ? 'hidden' : 'flex'}`} />
              QA/QC
            </TabsTrigger>
          )}
          {(ENABLE_STRICT_PERMISSION_CHECK ? canAccessDrawings === true : !!canAccessDrawings) && (
            <TabsTrigger value="drawings" className={tabTriggerClass}>
              <Ruler className={`h-4 w-4 ${isMobile ? 'hidden' : 'flex'}`} />
              Drawings
            </TabsTrigger>
          )}
          {canAccessPermit && (
            <TabsTrigger value="permit" className={tabTriggerClass}>
              <FileCheck className={`h-4 w-4 ${isMobile ? 'hidden' : 'flex'}`} />
              Permit
            </TabsTrigger>
          )}
        </TabGroupWrapper>

        {/* Group 3: Tài chính */}
        <TabGroupWrapper groupLabel="Finance" groupColor="#10B981" isActiveGroup={activeGroupId === 'finance'} showSeparator>
          {ENABLE_PROCUREMENT_TAB && (
            <TabsTrigger value="procurement" className={tabTriggerClass}>
              <Building2 className={`h-4 w-4 ${isMobile ? 'hidden' : 'flex'}`} />
              Procurement
            </TabsTrigger>
          )}
          {ENABLE_PAYMENT_DOCS_TAB && (
            <TabsTrigger value="payment-docs" className={tabTriggerClass}>
              <Receipt className={`h-4 w-4 ${isMobile ? 'hidden' : 'flex'}`} />
              PayDocs
            </TabsTrigger>
          )}
          {ENABLE_COLLECTION_TAB && (
            <TabsTrigger value="collection" className={tabTriggerClass}>
              <DollarSign className={`h-4 w-4 ${isMobile ? 'hidden' : 'flex'}`} />
              Collection
            </TabsTrigger>
          )}
          {ENABLE_PL_TAB && (
            <TabsTrigger value="pl" className={tabTriggerClass}>
              <TrendingUp className={`h-4 w-4 ${isMobile ? 'hidden' : 'flex'}`} />
              P&L
            </TabsTrigger>
          )}
        </TabGroupWrapper>

        {/* Group 4: Tổng kết */}
        <TabGroupWrapper groupLabel="Monitor & Control" groupColor="#8B5CF6" isActiveGroup={activeGroupId === 'summary'} showSeparator>
          {ENABLE_MEETING_TAB && (
            <TabsTrigger value="meeting" className={tabTriggerClass}>
              <Calendar className={`h-4 w-4 ${isMobile ? 'hidden' : 'flex'}`} />
              Meeting
            </TabsTrigger>
          )}
          {ENABLE_CLOSING_TAB && canAccessClosing && (
            <TabsTrigger value="closing" className={tabTriggerClass}>
              <Flag className={`h-4 w-4 ${isMobile ? 'hidden' : 'flex'}`} />
              Closing
            </TabsTrigger>
          )}
          {canAccessReports && (
            <TabsTrigger value="reports" className={tabTriggerClass}>
              <FileBarChart className={`h-4 w-4 ${isMobile ? 'hidden' : 'flex'}`} />
              Reports
            </TabsTrigger>
          )}
          {!HIDE_CONTROL_UI && ENABLE_SIGN_OFF_SYSTEM && (
            <TabsTrigger value="control" className={tabTriggerClass}>
              <Shield className={`h-4 w-4 ${isMobile ? 'hidden' : 'flex'}`} />
              Control
            </TabsTrigger>
          )}
          {ENABLE_ACTIVITY_LOG && canAccessActivity && (
            <TabsTrigger value="activity" className={tabTriggerClass}>
              <Activity className={`h-4 w-4 ${isMobile ? 'hidden' : 'flex'}`} />
              Activity
            </TabsTrigger>
          )}
          {ENABLE_AI_ADVISOR_TAB && (
            <TabsTrigger value="ai-advisor" className={tabTriggerClass}>
              <Bot className={`h-4 w-4 ${isMobile ? 'hidden' : 'flex'}`} />
              {isMobile ? 'AI' : 'AI Advisor'}
            </TabsTrigger>
          )}
          {isServiceProject && (
            <TabsTrigger value="maintenance" className={tabTriggerClass}>
              <Wrench className={`h-4 w-4 ${isMobile ? 'hidden' : 'flex'}`} />
              {isMobile ? 'MT' : 'Maintain'}
            </TabsTrigger>
          )}
        </TabGroupWrapper>

      </TabsList>

      {/* ✅ Phase 1 Fix: Use forceMount to preserve tab state when switching */}
      {/* Core tabs use forceMount for state preservation; heavy tabs (diary/drawings) lazy-load on first visit */}
      
      {canAccessNeeds && (
        <TabsContent value="needs" forceMount className={safeActiveTab !== 'needs' ? 'hidden' : 'mt-4'}>
          {(safeActiveTab === 'needs' || visited.has('needs')) ? (
            <Suspense fallback={<TabLoadingFallback />}>
              <NeedsLayout projectId={project.id} />
            </Suspense>
          ) : null}
        </TabsContent>
      )}
      
      {(ENABLE_STRICT_PERMISSION_CHECK ? canAccessPlan === true : !!canAccessPlan) && (
        <TabsContent value="tasks" forceMount className={safeActiveTab !== 'tasks' ? 'hidden' : 'mt-4'}>
          {(safeActiveTab === 'tasks' || visited.has('tasks')) ? (
            <AppErrorBoundary>
              <Suspense fallback={<TabLoadingFallback />}>
                <ProjectItemsManagement projectId={project.id} />
              </Suspense>
            </AppErrorBoundary>
          ) : null}
        </TabsContent>
      )}
      
      {(ENABLE_STRICT_PERMISSION_CHECK ? canAccessResource === true : !!canAccessResource) && (
        <TabsContent value="team" forceMount className={safeActiveTab !== 'team' ? 'hidden' : 'mt-4'}>
          {(safeActiveTab === 'team' || visited.has('team')) ? (
            <AppErrorBoundary>
              <Suspense fallback={<TabLoadingFallback />}>
                <ProjectTeamManagement projectId={project.id} />
              </Suspense>
            </AppErrorBoundary>
          ) : null}
        </TabsContent>
      )}
      
      {/* Diary/Execute: Heavy tab - only mount when first visited, then keep mounted */}
      {(ENABLE_STRICT_PERMISSION_CHECK ? canAccessExecute === true : !!canAccessExecute) && (
        <TabsContent value="diary" forceMount className={safeActiveTab !== 'diary' ? 'hidden' : 'mt-4'}>
          {(safeActiveTab === 'diary' || visited.has('diary')) ? (
            <AppErrorBoundary>
              <Suspense fallback={<TabLoadingFallback />}>
                <ProjectDiary projectId={project.id} />
              </Suspense>
            </AppErrorBoundary>
          ) : null}
        </TabsContent>
      )}
      
      {(ENABLE_STRICT_PERMISSION_CHECK ? canAccessSpecs === true : !!canAccessSpecs) && (
        <TabsContent value="specifications" forceMount className={safeActiveTab !== 'specifications' ? 'hidden' : 'mt-4'}>
          {(safeActiveTab === 'specifications' || visited.has('specifications')) ? (
            <AppErrorBoundary>
              <Suspense fallback={<TabLoadingFallback />}>
                <SpecificationsView projectId={project.id} />
              </Suspense>
            </AppErrorBoundary>
          ) : null}
        </TabsContent>
      )}
      
      {canAccessQAQC && (
        <TabsContent value="qaqc" forceMount className={safeActiveTab !== 'qaqc' ? 'hidden' : 'mt-4'}>
          {(safeActiveTab === 'qaqc' || visited.has('qaqc')) ? (
            <AppErrorBoundary>
              <Suspense fallback={<TabLoadingFallback />}>
                <QAQCView projectId={project.id} />
              </Suspense>
            </AppErrorBoundary>
          ) : null}
        </TabsContent>
      )}
      
      {ENABLE_PROCUREMENT_TAB && (
        <TabsContent value="procurement" forceMount className={safeActiveTab !== 'procurement' ? 'hidden' : 'mt-4'}>
          {(safeActiveTab === 'procurement' || visited.has('procurement')) ? (
            <Suspense fallback={<TabLoadingFallback />}>
              <OptimizedProcurementManagement projectId={project.id} />
            </Suspense>
          ) : null}
        </TabsContent>
      )}

      {ENABLE_PAYMENT_DOCS_TAB && (
        <TabsContent value="payment-docs" forceMount className={safeActiveTab !== 'payment-docs' ? 'hidden' : 'mt-4'}>
          {(safeActiveTab === 'payment-docs' || visited.has('payment-docs')) ? (
            <AppErrorBoundary>
              <Suspense fallback={<TabLoadingFallback />}>
                <PaymentDocsTab projectId={project.id} />
              </Suspense>
            </AppErrorBoundary>
          ) : null}
        </TabsContent>
      )}
      
      {ENABLE_COLLECTION_TAB && (
        <TabsContent value="collection" forceMount className={safeActiveTab !== 'collection' ? 'hidden' : 'mt-4'}>
          {(safeActiveTab === 'collection' || visited.has('collection')) ? (
            <AppErrorBoundary>
              <Suspense fallback={<TabLoadingFallback />}>
                <CollectionManagement projectId={project.id} />
              </Suspense>
            </AppErrorBoundary>
          ) : null}
        </TabsContent>
      )}
      
      {ENABLE_PL_TAB && (
        <TabsContent value="pl" forceMount className={safeActiveTab !== 'pl' ? 'hidden' : 'mt-4'}>
          {(safeActiveTab === 'pl' || visited.has('pl')) ? (
            <AppErrorBoundary>
              <Suspense fallback={<TabLoadingFallback />}>
                <PLManagement projectId={project.id} />
              </Suspense>
            </AppErrorBoundary>
          ) : null}
        </TabsContent>
      )}
      
      {/* Drawings: Heavy tab - forceMount for state preservation */}
      {(ENABLE_STRICT_PERMISSION_CHECK ? canAccessDrawings === true : !!canAccessDrawings) && (
        <TabsContent value="drawings" forceMount className={safeActiveTab !== 'drawings' ? 'hidden' : 'mt-4'}>
          {(safeActiveTab === 'drawings' || visited.has('drawings')) ? (
            <Suspense fallback={<TabLoadingFallback />}>
              <DrawingsLayout projectId={project.id} />
            </Suspense>
          ) : null}
        </TabsContent>
      )}
      
      {canAccessPermit && (
        <TabsContent value="permit" forceMount className={safeActiveTab !== 'permit' ? 'hidden' : 'mt-4'}>
          {(safeActiveTab === 'permit' || visited.has('permit')) ? (
            <Suspense fallback={<TabLoadingFallback />}>
              <PermitLayout projectId={project.id} />
            </Suspense>
          ) : null}
        </TabsContent>
      )}
      
      {ENABLE_MEETING_TAB && (
        <TabsContent value="meeting" forceMount className={safeActiveTab !== 'meeting' ? 'hidden' : 'mt-4'}>
          {(safeActiveTab === 'meeting' || visited.has('meeting')) ? (
            <AppErrorBoundary>
              <Suspense fallback={<TabLoadingFallback />}>
                <MeetingManagement projectId={project.id} />
              </Suspense>
            </AppErrorBoundary>
          ) : null}
        </TabsContent>
      )}
      
      {ENABLE_CLOSING_TAB && canAccessClosing && (
        <TabsContent value="closing" forceMount className={safeActiveTab !== 'closing' ? 'hidden' : 'mt-4'}>
          {(safeActiveTab === 'closing' || visited.has('closing')) ? (
            <AppErrorBoundary>
              <Suspense fallback={<TabLoadingFallback />}>
                <ClosingManagement projectId={project.id} />
              </Suspense>
            </AppErrorBoundary>
          ) : null}
        </TabsContent>
      )}

      {canAccessReports && (
        <TabsContent value="reports" forceMount className={safeActiveTab !== 'reports' ? 'hidden' : 'mt-4'}>
          {(safeActiveTab === 'reports' || visited.has('reports')) ? (
            <AppErrorBoundary>
              <Suspense fallback={<TabLoadingFallback />}>
                <ReportsManagement projectId={project.id} />
              </Suspense>
            </AppErrorBoundary>
          ) : null}
        </TabsContent>
      )}
      
      {!HIDE_CONTROL_UI && ENABLE_SIGN_OFF_SYSTEM && (
        <TabsContent value="control" forceMount className={safeActiveTab !== 'control' ? 'hidden' : 'mt-4'}>
          {(safeActiveTab === 'control' || visited.has('control')) ? (
            <AppErrorBoundary>
              <Suspense fallback={<TabLoadingFallback />}>
                <ControlDashboard projectId={project.id} />
              </Suspense>
            </AppErrorBoundary>
          ) : null}
        </TabsContent>
      )}
      
      {ENABLE_ACTIVITY_LOG && canAccessActivity && (
        <TabsContent value="activity" forceMount className={safeActiveTab !== 'activity' ? 'hidden' : 'mt-4'}>
          {(safeActiveTab === 'activity' || visited.has('activity')) ? (
            <AppErrorBoundary>
              <Suspense fallback={<TabLoadingFallback />}>
                <ProjectActivityLog projectId={project.id} />
              </Suspense>
            </AppErrorBoundary>
          ) : null}
        </TabsContent>
      )}
      
      {ENABLE_AI_ADVISOR_TAB && (
        <TabsContent value="ai-advisor" forceMount className={safeActiveTab !== 'ai-advisor' ? 'hidden' : 'mt-4'}>
          {(safeActiveTab === 'ai-advisor' || visited.has('ai-advisor')) ? (
            <Suspense fallback={<TabLoadingFallback />}>
              <AIAdvisorInlineTabs projectId={project.id} />
            </Suspense>
          ) : null}
        </TabsContent>
      )}

      {isServiceProject && (
        <TabsContent value="maintenance" forceMount className={safeActiveTab !== 'maintenance' ? 'hidden' : 'mt-4'}>
          {(safeActiveTab === 'maintenance' || visited.has('maintenance')) ? (
            <AppErrorBoundary>
              <Suspense fallback={<TabLoadingFallback />}>
                <MaintainSection project={project} permissions={permissions} />
              </Suspense>
            </AppErrorBoundary>
          ) : null}
        </TabsContent>
      )}

      <TabsContent value="method" forceMount
        className={safeActiveTab !== 'method' ? 'hidden' : 'mt-4 focus-visible:ring-0 focus-visible:ring-offset-0'}>
        {(safeActiveTab === 'method' || visited.has('method')) ? (
          <AppErrorBoundary>
            <Suspense fallback={<TabLoadingFallback />}>
              <MethodSection project={project} permissions={permissions} isActive={safeActiveTab === 'method'} />
            </Suspense>
          </AppErrorBoundary>
        ) : null}
      </TabsContent>

      <TabsContent value="layout" forceMount
        className={safeActiveTab !== 'layout' ? 'hidden' : 'mt-4 focus-visible:ring-0 focus-visible:ring-offset-0'}>
        {(safeActiveTab === 'layout' || visited.has('layout')) ? (
          <AppErrorBoundary>
            <Suspense fallback={<TabLoadingFallback />}>
              <LayoutSection project={project} permissions={permissions} isActive={safeActiveTab === 'layout'} />
            </Suspense>
          </AppErrorBoundary>
        ) : null}
      </TabsContent>

      <TabsContent value="scope" forceMount
        className={safeActiveTab !== 'scope' ? 'hidden' : 'mt-4 focus-visible:ring-0 focus-visible:ring-offset-0'}>
        {(safeActiveTab === 'scope' || visited.has('scope')) ? (
          <AppErrorBoundary>
            <Suspense fallback={<TabLoadingFallback />}>
              <LayoutSection project={project} permissions={permissions} isActive={safeActiveTab === 'scope'} surface="scope" />
            </Suspense>
          </AppErrorBoundary>
        ) : null}
      </TabsContent>
    </Tabs>
  );
};

// Debounce helper
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export default ProjectContent;